/**
 * `<select class="lvl">` đổi mức quyền tại chỗ — FR-072, FR-076.
 * docs/08 §8.5: thao tác nhanh dùng optimistic update, rollback khi lỗi.
 *
 * Prototype liệt kê đủ 4 mức, kể cả *Chủ sở hữu*. Mức `owner` **không gán được**
 * — nó chỉ sinh ra khi người ta tự thêm tên miền bằng kết nối của mình
 * (docs/05 §5.5), nên select không liệt kê nó.
 *
 * Trước OQ-245, chọn *Chủ sở hữu* sẽ mở hộp thoại chuyển quyền sở hữu. Chức
 * năng ấy đã gỡ hẳn: chủ sở hữu đi theo kết nối đã thêm tên miền, không chuyển
 * tay được.
 */

import { useEffect, useState } from 'react';
import { useT } from '../../lib/i18n';
import type { AssignableDomainRoleView } from './api';

export function GrantLevelSelect({
  value,
  roles,
  disabled,
  onChange,
  onError,
  ariaLabel,
}: {
  value: string;
  roles: AssignableDomainRoleView[];
  disabled?: boolean;
  onChange: (domainRoleId: string) => Promise<void>;
  /**
   * Bat buoc bao loi ra ngoai: 403 cua SEC-8 (chong tu nang quyen),
   * 409 OWNER_IMMUTABLE, 404, 422… deu chi lam select bat ve gia tri cu.
   * Nuot loi o day thi nguoi dung khong hieu vi sao thao tac khong an.
   */
  onError: (err: unknown) => void;
  ariaLabel: string;
}) {
  const [current, setCurrent] = useState(value);
  const [busy, setBusy] = useState(false);

  useEffect(() => setCurrent(value), [value]);

  const ownerRoleId = roles.find((role) => !role.assignable)?.id;

  return (
    <select
      className="lvl"
      aria-label={ariaLabel}
      value={current}
      disabled={disabled || busy}
      onChange={async (event) => {
        const next = event.target.value;

        /*
         * Mức owner vẫn lọt vào danh sách khi nó đang là mức hiện tại — nếu
         * không thì `<select>` mang một `value` không có `<option>` nào khớp.
         * Chọn lại chính nó thì không có gì để làm.
         */
        if (next === ownerRoleId) {
          event.target.value = current;
          return;
        }

        const previous = current;
        setCurrent(next); // optimistic
        setBusy(true);
        try {
          await onChange(next);
        } catch (err) {
          setCurrent(previous); // rollback
          onError(err);
        } finally {
          setBusy(false);
        }
      }}
    >
      {roles
        .filter((role) => role.assignable || role.id === current)
        .map((role) => (
          <option key={role.id} value={role.id}>
            {role.name}
          </option>
        ))}
    </select>
  );
}

/** Chip quan hệ — "Người thêm" / "Được cấp" / "Toàn quyền hệ thống". */
export function RelationChip({ relation }: { relation: 'owned' | 'granted' | 'viewall' }) {
  const t = useT();
  if (relation === 'owned') {
    return <span className="chip accent tag-role">{t('perm.relation.owned')}</span>;
  }
  if (relation === 'viewall') {
    return <span className="chip info tag-role">{t('perm.relation.viewall')}</span>;
  }
  return <span className="chip tag-role">{t('perm.relation.granted')}</span>;
}
